import React, { useEffect, useRef, useState } from 'react';

import styles from './site_filter.module.css';

const summaryLabel = (sites, selectedIds) => {
  if (selectedIds.length === 0) {
    return 'All sites';
  }

  if (selectedIds.length === 1) {
    const site = sites.find((candidate) => candidate.id === selectedIds[0]);
    return site ? site.site_name : '1 site';
  }

  return `${selectedIds.length} sites`;
};

// A multi-select rather than a single-site dropdown, because a monitor covering several sites
// usually wants a subset rather than one site or everything. Only the sites the user has been
// granted are offered, so the filter can never reach past what the server already allows.
export default function SiteFilter({ sites, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const toggle = (siteId) => {
    if (selectedIds.includes(siteId)) {
      onChange(selectedIds.filter((id) => id !== siteId));
      return;
    }

    onChange([...selectedIds, siteId]);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <span className={styles.label} id='reportable-events-site-filter-label'>
        Site
      </span>
      <button
        aria-expanded={open}
        aria-haspopup='true'
        aria-labelledby='reportable-events-site-filter-label reportable-events-site-filter-summary'
        className={selectedIds.length > 0 ? `${styles.trigger} ${styles.active}` : styles.trigger}
        onClick={() => setOpen(!open)}
        type='button'
      >
        <span id='reportable-events-site-filter-summary'>{summaryLabel(sites, selectedIds)}</span>
        <svg aria-hidden='true' className={styles.caret} viewBox='0 0 12 12'>
          <path d='M2.5 4.5L6 8L9.5 4.5' fill='none' stroke='currentColor' strokeLinecap='round' strokeWidth='1.5' />
        </svg>
      </button>

      {open && (
        <div className={styles.popover} role='group' aria-label='Filter by site'>
          {sites.length === 0 && <p className={styles.no_sites}>You have not been assigned to any sites.</p>}

          {sites.map((site) => (
            <label className={styles.option} key={site.id}>
              <input checked={selectedIds.includes(site.id)} onChange={() => toggle(site.id)} type='checkbox' />
              <span className={styles.option_name}>{site.site_name}</span>
              <span className={styles.option_id}>{site.site_id}</span>
            </label>
          ))}

          {selectedIds.length > 0 && (
            <button className={styles.clear} onClick={() => onChange([])} type='button'>
              Show all sites
            </button>
          )}
        </div>
      )}
    </div>
  );
}
